import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { snapshotsAPI, normalizeSnapshotList } from '../../data/snapshots';
import { unwrap } from '../../data/api';
import { LIST_STALE_TIME, OVERVIEW_STALE_TIME } from '../../lib/queryDefaults';
import { invalidateFinancialData } from '../financialCache';
import { invalidateGuidedFlow } from '../guidedFlow/queries';
import { normalizeSelfieCycles } from '../../utils/selfieCycles';

export const snapshotsKeys = {
  all: ['snapshots'],
  list: (params) => [...snapshotsKeys.all, 'list', params || {}],
  current: () => [...snapshotsKeys.all, 'current'],
  cycles: () => [...snapshotsKeys.all, 'cycles'],
  timeline: (params) => [...snapshotsKeys.all, 'timeline', params || {}],
  // CHANGE v2 §11: cache key para o detalhe.
  detail: (id) => [...snapshotsKeys.all, 'detail', String(id)],
  report: (id) => [...snapshotsKeys.all, 'report', String(id)],
};


export function useSnapshotsListQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: snapshotsKeys.list(params),
    queryFn: () =>
      snapshotsAPI.listSnapshots(params).then((r) => normalizeSnapshotList(unwrap(r, []))),
    ...queryOptions,
  });
}

export function useCurrentSnapshotQuery(queryOptions = {}) {
  return useQuery({
    queryKey: snapshotsKeys.current(),
    queryFn: () => snapshotsAPI.currentSnapshot().then((r) => unwrap(r, null)),
    staleTime: OVERVIEW_STALE_TIME,
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}

export function useSnapshotsTimelineQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: snapshotsKeys.timeline(params),
    queryFn: () =>
      snapshotsAPI.netWorthTimeline(params).then((r) => unwrap(r, [])),
    staleTime: OVERVIEW_STALE_TIME,
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}

export function useSelfieCyclesQuery(queryOptions = {}) {
  return useQuery({
    queryKey: snapshotsKeys.cycles(),
    queryFn: () =>
      snapshotsAPI.listCycles().then((r) => normalizeSelfieCycles(unwrap(r, []))),
    staleTime: LIST_STALE_TIME,
    placeholderData: (prev) => prev,
    ...queryOptions,
  });
}

export function usePatchCurrentCycleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ start, end }) =>
      snapshotsAPI.patchCurrentCycle({ start, end }).then((r) => unwrap(r)),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: snapshotsKeys.cycles() });
      await invalidateFinancialData(qc);
    },
  });
}

/** Actualiza só o início (ex.: ao fechar selfie); o fim mantém-se no servidor. */
export function usePatchCurrentCycleStartMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (start) =>
      snapshotsAPI.patchCurrentCycle({ start }).then((r) => unwrap(r)),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: snapshotsKeys.cycles() });
      await invalidateFinancialData(qc);
    },
  });
}

function invalidateSnapshotCaches(qc) {
  qc.invalidateQueries({ queryKey: snapshotsKeys.all, exact: false });
  qc.invalidateQueries({ queryKey: ['accounts'], exact: false });
  qc.invalidateQueries({ queryKey: ['emergencyFund'], exact: false });
  qc.invalidateQueries({ queryKey: ['liabilities'], exact: false });
  invalidateGuidedFlow(qc);
  void invalidateFinancialData(qc);
}

export function useEmergencyFundReviewQuery(snapshotId, queryOptions = {}) {
  return useQuery({
    queryKey: [...snapshotsKeys.all, 'efReview', String(snapshotId)],
    queryFn: () =>
      snapshotsAPI.emergencyFundReview(snapshotId).then((r) => unwrap(r)),
    enabled: Boolean(snapshotId),
    ...queryOptions,
  });
}

export function useCreateSnapshotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      snapshotsAPI.createSnapshot(payload).then((r) => unwrap(r)),
    onSuccess: async () => {
      invalidateSnapshotCaches(qc);
      await qc.refetchQueries({ queryKey: snapshotsKeys.cycles() });
    },
  });
}

// CHANGE v2 §11: queries/mutations para editar selfies anteriores.
export function useSnapshotDetailQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: snapshotsKeys.detail(id),
    queryFn: () => snapshotsAPI.getSnapshot(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function useSelfieReportQuery(id, queryOptions = {}) {
  const { refetchInterval: userInterval, ...restOptions } = queryOptions;
  return useQuery({
    queryKey: snapshotsKeys.report(id),
    queryFn: () => snapshotsAPI.getReport(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    // staleTime 0: report status polling (pending/regenerating)
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'pending' || status === 'regenerating') {
        return userInterval ?? 1500;
      }
      return false;
    },
    ...restOptions,
  });
}

export function useUpdateSnapshotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      snapshotsAPI.updateSnapshot(id, payload).then((r) => unwrap(r)),
    onSuccess: (data, vars) => {
      if (data) qc.setQueryData(snapshotsKeys.detail(vars.id), data);
      invalidateSnapshotCaches(qc);
    },
  });
}

export function useDeleteSnapshotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => snapshotsAPI.deleteSnapshot(id).then((r) => unwrap(r)),
    onSuccess: async () => {
      invalidateSnapshotCaches(qc);
      await qc.refetchQueries({ queryKey: snapshotsKeys.cycles() });
    },
  });
}

export function useUpdateAccountSnapshotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      snapshotsAPI.updateAccountSnapshot(id, payload).then((r) => unwrap(r)),
    onSuccess: () => invalidateSnapshotCaches(qc),
  });
}

/**
 * Returns a map of `account_id → balance_native` from the most recent
 * completed snapshot of the user. Used by the selfie UI to fill in the
 * "Igual ao mês anterior" shortcut.
 */
export function usePreviousSnapshotEntries() {
  const { data: current } = useCurrentSnapshotQuery();
  if (!current?.account_snapshots) return {};
  const map = {};
  for (const row of current.account_snapshots) {
    map[row.account] = {
      balance_native: row.balance_native,
      price_per_unit: row.price_per_unit,
    };
  }
  return map;
}

/** Map of liability_id → previous snapshot values for selfie copy shortcut. */
export function usePreviousLiabilitySnapshotEntries() {
  const { data: current } = useCurrentSnapshotQuery();
  if (!current?.liability_snapshots) return {};
  const map = {};
  for (const row of current.liability_snapshots) {
    map[row.liability] = {
      remaining_balance_eur: row.remaining_balance_eur,
      amount_paid_this_month: row.amount_paid_this_month,
      interest_paid_this_month: row.interest_paid_this_month,
    };
  }
  return map;
}
